/* eslint-disable react-hooks/rules-of-hooks */
import {
    useDomainContext,
    useForm,
    useFormContext,
    useScrollToTop,
} from "@hooks";
import { ClickOffer, Field, ListingItem } from "@lib/shared/types";
import { useRouter } from "next/router";
import { ReactElement, useRef, useState } from "react";
import FormSkeleton from "./consentOnlyForm/FormSkeleton";
import FieldWrapper from "./fieldWrapper";
import {
    ExitOffersModal,
    FormInOfferModal,
    FormStepsWrapper,
    InlineThankYouShortcut,
    PreFirstStepTitle,
} from "./formNew/components";
import {
    InvalidFieldWithValue,
    useBlockOtherSections,
    useExitOffersModal,
    useFormPersistence,
    useFormInit,
    useFormSideFetches,
    useFormTcpa,
    useInvalidFields,
    useModalVariables,
    usePreventPageClose,
    useSecondServiceState,
    useSlideFormValue,
    useThankYouRedirect,
    useFormRenderConditions,
} from "./formNew/hooks";
import {
    getExpandableFields as getFieldExpandableFields,
    isExpandable as isFieldExpandable,
} from "./formNew/utils";
import { FormProps } from "./index";
import OffersInForm from "./offersInForm";
import ProgressBar from "./progressBar";
import SecondServiceForms from "./secondService";
import styles from "./styles.module.scss";

export default function Form(props: FormProps): ReactElement {
    const {
        form,
        domain,
        category,
        formTemplate,
        formOffer,
        visitDetails,
        exitModalOffer,
        locale,
        piiCompletionData,
        routeConfig,
        buyerPhoneNumber,
        defaultLocale,
        domainNameTranslation,
        loadOneToOneForm,
    } = useDomainContext();
    const {
        setSetPopupFormSubmittedFunction,
        skipToThankYou,
        setSecondServiceSubmitted,
        setGoToThankYouPage,
        formSubmitted: isMainFormSubmitted,
    } = useFormContext();

    const router = useRouter();
    const [offersPopupActive, setOffersPopupActive] = useState(false);
    const [popupOffers, setPopupOffers] = useState<ListingItem[]>([]);
    const prevStepRef = useRef<number | null>(null);
    const [clicksOffers, setClicksOffers] = useState<
        ClickOffer[] | null | undefined
    >([]);
    const [listRequestId, setListRequestId] = useState("");

    useFormInit({
        router,
        visitDetails,
        domain,
        goToThankYouPage: props.goToThankYouPage,
        setGoToThankYouPage,
    });

    const compactOfferBlock = domain.defaultOffers?.find(
        (offer) => offer.category?.slug === category?.slug,
    );
    const { form: formAsProp } = props;
    const finalForm = formAsProp ?? form;
    if (finalForm) {
        const {
            classes,
            colors,
            showProgress,
            progressPosition,
            progressMode,
            progressTitle,
            progressIcon,
            backButton,
            sellingPoint,
            sellingPointsSteps,
            preventWindowClose,
            progressShift,
            stepsConfig,
            goToThankYouPage,
            dynamicThankYouFields,
            scrollToTop = true,
            modalSize,
            currentCategorySlug,
            popupStyle,
            hideOffers,
            customThankYouStep,
            isSecondServiceForm,
            isPiiCompletionForm,
            secondServiceColors,
            secondServiceClasses,
            updateSecondServices,
            tags,
            activeSecondServices,
            secondServiceLoading,
            showStepNumberOnly,
            formTemplateProp,
            resetLeadId = false,
            searchableMultiSelect,
            secondServiceSearchableMultiSelect,
            onModalClickClose,
            onInlineThankYouShown,
            ignoreCategoryInThankYouPagePath = false,
            isFormInsideOffersModal = false,
            goToListPage,
            initialFieldData,
            progressBesideBackButton,
            singleQuestion,
            singleQuestionMaxOptions,
            urlToRedirectFromSingleQuestion,
            mergeInputWithSubmitOnFirstStep,
            enableApiCall,
            apiCallField,
            disableExitModal,
            disableAutoFocus,
            isGeneralModalForm,
            customOfferExitModal,
            customOfferExitModalImage,
            customOfferExitModalContent,
        } = props;

        const isFormSubmitted = useRef(false);

        // Synchronously keep the ref in sync during render so the beforeunload
        // guard sees it before window.location.href fires (effects are too late).
        if (isMainFormSubmitted && !isFormInsideOffersModal) {
            isFormSubmitted.current = true;
        }

        const {
            handleFieldBlur,
            handleFieldChange,
            handleSubmit,
            handlePrevStep,
            formId,
            formError,
            jornayaIdElement,
            steps,
            currentStep,
            isLoading,
            fieldsData,
            successMessage,
            handleOutsideFormSubmit,
            formSubmitted,
            otp,
            checkIfStepHasOneField,
            visitedSteps,
            formErrors,
            formErrorsResolved,
            showExitModal,
            stepStatus,
            showRegulations,
            setLoading,
            setHasRegulations,
            showCustomListingModal,
            setShowCustomListingModal,
            showPiiCompletionOtherFields,
            setShowPiiCompletionOtherFields,
            showFormModal,
            toggleFormModal,
            listingSlug,
            toggleShowFormInExitModal,
            showFromInExitModal,
            handleCloseFormInsideExitModal,
            dispatch,
            startSecondRoundBidding,
            operation,
            globalLoading,
            apiCallLoading,
            reloadFormData,
            isLastStep,
        } = useForm(
            finalForm,
            isSecondServiceForm,
            isPiiCompletionForm,
            goToListPage,
            resetLeadId,
            scrollToTop,
            !!props.showInModal,
            isFormInsideOffersModal,
            isFormSubmitted,
            initialFieldData,
            singleQuestion,
            urlToRedirectFromSingleQuestion,
            isGeneralModalForm,
        );

        useFormPersistence({
            fieldsData,
            dynamicThankYouFields,
            isSecondServiceForm,
            isPiiCompletionForm,
            piiCompletionData,
            successMessage,
            formId: finalForm.id,
            isFormInsideOffersModal,
            reloadFormData: reloadFormData as (() => void) | undefined,
        });

        useBlockOtherSections({
            routeConfig,
            domain,
            category,
            currentStep,
            prevStepRef,
        });

        const { isSecondServiceSelected } = useSecondServiceState({
            isSecondServiceForm,
            formSubmitted,
            setSecondServiceSubmitted,
            tags,
        });

        const scrollToTopHandler = useScrollToTop({ enabled: scrollToTop });

        useFormSideFetches({
            domain,
            category,
            fieldsData,
            showRegulations,
            showCustomListingModal,
            listingSlug,
            setLoading,
            setOffersPopupActive,
            setClicksOffers,
            setListRequestId,
            setPopupOffers,
        });

        const {
            handleOffersPopupVisibility,
            modalOffersContent,
            modalOfferClicksContent,
            modalWidth,
        } = useExitOffersModal({
            exitModalOffer,
            category,
            currentCategorySlug: props.currentCategorySlug,
            buyerPhoneNumber,
            routeConfig,
            showFormModal,
            isFormInsideOffersModal,
            singleQuestion,
            showFromInExitModal,
            showExitModal,
            showRegulations,
            clicksOffers,
            listRequestId,
            visitDetails,
            popupOffers,
            popupStyle,
            offersPopupActive,
            setOffersPopupActive,
            setPopupOffers,
            toggleShowFormInExitModal,
            disableExitModal,
            customOfferExitModal,
            customOfferExitModalImage,
            customOfferExitModalContent,
        });

        usePreventPageClose({
            isFormSubmitted,
            preventWindowClose,
            router,
            visitDetails,
            isLastStep,
            setSetPopupFormSubmittedFunction,
            handleOutsideFormSubmit,
            handleOffersPopupVisibility,
            currentCategorySlug,
            exitModalOffer,
            buyerPhoneNumber,
        });

        useThankYouRedirect({
            formSubmitted,
            goToThankYouPage,
            skipToThankYou,
            otp,
            category,
            isSecondServiceForm,
            dynamicThankYouFields,
            fieldsData,
            loadOneToOneForm,
            operation,
            router,
            isFormSubmitted,
            domain,
            locale,
            defaultLocale: defaultLocale ?? undefined,
            form: finalForm,
            ignoreCategoryInThankYouPagePath,
            onInlineThankYouShown,
        });

        const slideFormValue: number = useSlideFormValue({
            isLastStep,
            formErrors,
            formErrorsResolved,
            operation,
            formSubmitted,
            goToThankYouPage,
            goToListPage,
            otp,
            currentStep,
        });

        const {
            finalAnswerSummaryConfigs,
            shouldShowProgressBar,
            shouldShowBackButton,
            progressTotal,
            shouldShowPreFirstStepTitle,
            shouldShowOffersInFormTop,
            shouldShowOffersInFormBottom,
            shouldShowFieldWrapperProgress,
            shouldShowFieldWrapperBackButton,
            shouldShowSellingPoint,
            sellingPointLabel,
            shouldShowFloatingTcpa,
            handleBackClick,
            shouldShowFormInOffersExitModal,
            showOtp,
            showTCPAMessage,
            shouldShowSkipThankYouShortcut,
            shouldRenderSecondServiceForm,
            submitButtonText,
        } = useFormRenderConditions({
            showProgress,
            globalLoading,
            otp,
            formErrors,
            showFormModal,
            operation,
            stepsConfig,
            currentStep,
            formSubmitted,
            steps,
            domain,
            preFirstStepTitle: props.preFirstStepTitle,
            formTemplate,
            formTemplateProp,
            sellingPointsSteps,
            sellingPoint,
            isLastStep,
            visitDetails,
            locale,
            backButton,
            handlePrevStep,
            isFormInsideOffersModal,
            goToListPage,
            category,
            formOffer,
            form,
            isMainFormSubmitted,
            isPiiCompletionForm,
            isSecondServiceForm,
            skipToThankYou,
            goToThankYouPage,
        });

        const tcpa = useFormTcpa({
            isSecondServiceForm,
            isSecondServiceSelected,
            showTCPAMessage,
            formErrors,
            operation,
            domain,
            finalForm,
            steps,
            currentStep,
            classes,
            submitButtonText,
            dispatch,
            isFormInsideOffersModal,
        });

        const invalidFields: InvalidFieldWithValue[] = useInvalidFields({
            steps,
            singleQuestion,
            singleQuestionMaxOptions,
            formErrors,
            fieldsData,
        });

        const modalVariables = useModalVariables(routeConfig);

        const isExpandable: (field: Field) => boolean = isFieldExpandable;
        const getExpandableFields: (field: Field) => Field[] = (field: Field) =>
            getFieldExpandableFields(field, fieldsData);

        if (shouldShowSkipThankYouShortcut) {
            scrollToTopHandler();
            return (
                <InlineThankYouShortcut
                    formClassName={classes?.formClassName}
                    compactOfferBlock={compactOfferBlock}
                    hideOffers={hideOffers}
                    customThankYouStep={customThankYouStep}
                    innerThankYouClassName={classes?.innerThankYouClassName}
                />
            );
        }

        if (shouldRenderSecondServiceForm) {
            return (
                <SecondServiceForms
                    classes={secondServiceClasses}
                    colors={{ ...colors, ...secondServiceColors }}
                    goToThankYou={goToThankYouPage ? true : false}
                    secondServiceSearchableMultiSelect={
                        secondServiceSearchableMultiSelect
                    }
                />
            );
        }

        return (
            <>
                {finalForm.testConfig?.config?.css && (
                    <style
                        dangerouslySetInnerHTML={{
                            __html: finalForm.testConfig?.config?.css,
                        }}
                    />
                )}

                <form
                    data-tf-element-role="offer"
                    onSubmit={(e) => {
                        handleSubmit(e, {
                            isSecondForm: isSecondServiceForm ? true : false,
                            enableApiCall,
                            apiCallField: apiCallField as "zipcode" | undefined,
                        });
                    }}
                    className={`${classes?.formClassName ?? ""} ${
                        styles["form"]
                    }`}
                    style={{
                        color: colors.textColor,
                        ...(showFormModal
                            ? {
                                  backgroundColor: "transparent",
                                  boxShadow: "none",
                              }
                            : {}),
                    }}
                    noValidate
                    id={`form_${formId}`}
                    method="post"
                >
                    {shouldShowPreFirstStepTitle && (
                        <PreFirstStepTitle
                            title={props.preFirstStepTitle as string}
                            region={visitDetails.region}
                            city={visitDetails?.city}
                        />
                    )}
                    {shouldShowOffersInFormTop && (
                        <OffersInForm
                            classes={classes?.freeConsultationOfferClassName}
                            offer={formOffer?.length ? formOffer[0] : undefined}
                            shape={
                                formTemplate ?? formTemplateProp ?? undefined
                            }
                        />
                    )}

                    {shouldShowProgressBar && progressPosition === "top" && (
                        <ProgressBar
                            color={colors?.progressBar}
                            current={currentStep}
                            className={`${styles["top-progress-bar"]} ${
                                classes?.formTopProgressbarClassName ?? ""
                            }`}
                            total={progressTotal}
                            shift={progressShift}
                            mode={progressMode}
                            titleIcon={progressIcon}
                            title={progressTitle}
                            activeSegmentClassName={
                                classes?.activeSegmentClassName ?? ""
                            }
                        />
                    )}

                    {globalLoading && initialFieldData ? (
                        <FormSkeleton />
                    ) : (
                        <FieldWrapper
                            type={showFormModal ? "modal" : "div"}
                            classes={classes}
                            current={currentStep}
                            total={progressTotal}
                            colors={colors}
                            shift={progressShift}
                            showProgress={shouldShowFieldWrapperProgress}
                            showStepNumberOnly={showStepNumberOnly}
                            showBackButton={shouldShowFieldWrapperBackButton}
                            handlePrevStep={handlePrevStep}
                            backButton={backButton}
                            onModalCloseClick={() => {
                                toggleFormModal(false);
                                onModalClickClose?.();
                            }}
                            tcpa={tcpa}
                        >
                            <FormStepsWrapper
                                slideFormValue={slideFormValue}
                                steps={steps}
                                stepsConfig={stepsConfig}
                                formProps={props}
                                isSecondServiceForm={isSecondServiceForm}
                                operation={operation}
                                classes={classes}
                                isFormInsideOffersModal={
                                    isFormInsideOffersModal
                                }
                                formErrors={formErrors}
                                handleFieldBlur={handleFieldBlur}
                                handleFieldChange={handleFieldChange}
                                currentStep={currentStep}
                                isLoading={isLoading}
                                globalLoading={globalLoading}
                                formError={formError}
                                fieldsData={fieldsData}
                                visitedSteps={visitedSteps}
                                checkIfStepHasOneField={checkIfStepHasOneField}
                                formSubmitted={formSubmitted}
                                formId={formId}
                                finalForm={finalForm}
                                stepStatus={stepStatus}
                                form={form}
                                updateSecondServices={updateSecondServices}
                                tags={tags}
                                activeSecondServices={activeSecondServices}
                                secondServiceLoading={secondServiceLoading}
                                showPiiCompletionOtherFields={
                                    showPiiCompletionOtherFields
                                }
                                setShowPiiCompletionOtherFields={
                                    setShowPiiCompletionOtherFields
                                }
                                searchableMultiSelect={searchableMultiSelect}
                                finalAnswerSummaryConfigs={
                                    finalAnswerSummaryConfigs
                                }
                                tcpa={tcpa}
                                singleQuestion={singleQuestion}
                                mergeInputWithSubmitOnFirstStep={
                                    mergeInputWithSubmitOnFirstStep
                                }
                                apiCallLoading={apiCallLoading}
                                formErrorsResolved={formErrorsResolved}
                                invalidFields={invalidFields}
                                isExpandable={isExpandable}
                                getExpandableFields={getExpandableFields}
                                domain={domain}
                                domainNameTranslation={domainNameTranslation}
                                isLastStep={isLastStep}
                                showOtp={showOtp}
                                otp={otp}
                                dispatch={dispatch}
                                submitButtonText={submitButtonText}
                                startSecondRoundBidding={
                                    startSecondRoundBidding
                                }
                                goToThankYouPage={goToThankYouPage}
                                goToListPage={goToListPage}
                                compactOfferBlock={compactOfferBlock}
                                hideOffers={hideOffers}
                                customThankYouStep={customThankYouStep}
                                colors={colors}
                                isLoading2={isLoading}
                                disableAutoFocus={disableAutoFocus}
                            />
                        </FieldWrapper>
                    )}
                    {jornayaIdElement}
                    {shouldShowSellingPoint && (
                        <div
                            className={`${styles["sellingPoint"]} ${
                                classes?.sellingPointClassName ?? ""
                            }`}
                        >
                            {sellingPointLabel}
                        </div>
                    )}
                    {progressBesideBackButton && (
                        <div className={styles["progressBesideBackButton"]}>
                            {shouldShowBackButton && (
                                <button
                                    type="button"
                                    className={`${
                                        styles["back-button-beside"]
                                    } ${backButton?.class ?? ""}`}
                                    onClick={handleBackClick}
                                >
                                    {backButton?.label ?? "Previous"}
                                    <i className={styles["arrow-back"]}></i>
                                </button>
                            )}
                            {shouldShowProgressBar &&
                                progressPosition !== "top" && (
                                    <ProgressBar
                                        className={`${styles["progress-bar"]} ${
                                            classes?.formProgressbarClassName ??
                                            ""
                                        }`}
                                        current={currentStep}
                                        total={progressTotal}
                                        color={colors?.progressBar}
                                        shift={progressShift}
                                        showStepNumberOnly={showStepNumberOnly}
                                        style={progressMode}
                                    />
                                )}
                        </div>
                    )}
                    {shouldShowProgressBar &&
                        progressPosition !== "top" &&
                        !progressBesideBackButton && (
                            <ProgressBar
                                className={`${styles["progress-bar"]} ${
                                    classes?.formProgressbarClassName ?? ""
                                }`}
                                current={currentStep}
                                total={progressTotal}
                                color={colors?.progressBar}
                                shift={progressShift}
                                showStepNumberOnly={showStepNumberOnly}
                                style={progressMode}
                            />
                        )}
                    {shouldShowOffersInFormBottom && (
                        <OffersInForm
                            classes={classes?.freeConsultationOfferClassName}
                            offer={formOffer?.length ? formOffer[0] : undefined}
                            shape={
                                (formTemplate as string) ||
                                formTemplateProp ||
                                undefined
                            }
                        />
                    )}
                    {shouldShowFloatingTcpa && tcpa}
                    {shouldShowBackButton && !progressBesideBackButton && (
                        <button
                            type="button"
                            className={`ltrStyle ${styles["back-button"]} ${
                                backButton?.class ?? ""
                            }`}
                            onClick={handleBackClick}
                        >
                            {backButton?.label ?? "Previous"}
                            <i className={styles["arrow-back"]}></i>
                        </button>
                    )}
                    <ExitOffersModal
                        offersPopupActive={offersPopupActive}
                        buyerPhoneNumber={buyerPhoneNumber}
                        hidePageOffers={
                            routeConfig?.config?.settings?.hidePageOffers
                        }
                        onClose={() => {
                            setOffersPopupActive(false);
                            if (showRegulations) {
                                setClicksOffers([]);
                                setHasRegulations(false);
                            }
                            if (showCustomListingModal) {
                                setPopupOffers([]);
                                setShowCustomListingModal(false);
                            }
                        }}
                        showRegulations={showRegulations}
                        setClicksOffers={setClicksOffers}
                        setHasRegulations={setHasRegulations}
                        showCustomListingModal={showCustomListingModal}
                        setPopupOffers={setPopupOffers}
                        setShowCustomListingModal={setShowCustomListingModal}
                        modalSize={modalSize}
                        modalWidth={modalWidth}
                        modalVariables={modalVariables}
                        modalOffersContent={modalOffersContent}
                        modalOfferClicksContent={modalOfferClicksContent}
                        setOffersPopupActive={setOffersPopupActive}
                    />
                    {shouldShowFormInOffersExitModal && (
                        <FormInOfferModal
                            showFromInExitModal={showFromInExitModal}
                            handleCloseFormInsideExitModal={
                                handleCloseFormInsideExitModal
                            }
                            modalVariables={modalVariables}
                            formProps={props}
                        />
                    )}
                </form>
            </>
        );
    }

    return <></>;
}
